import React, { useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ReplyIcon from "@mui/icons-material/Reply";
import { useTranslation } from "react-i18next";
import {
  getChatbotInteractiveOptions,
  getChatbotLinkButtons,
} from "../../utils/chatbotQuickReplies";

const INLINE_BUTTON_LIMIT = 3;
const WA_GREEN = "#00a884";

/** Defaults em inglês vindos do WhatsApp/URA — sempre localizar no Web Chat. */
const EN_DEFAULT_LIST_BUTTONS = new Set([
  "see options",
  "choose an option",
  "select an option",
  "choose option",
]);

function resolveListButtonLabel(raw, t) {
  const s = String(raw || "").trim();
  if (!s || EN_DEFAULT_LIST_BUTTONS.has(s.toLowerCase())) {
    return t("webchat.seeOptions");
  }
  return s;
}
function listSectionsFromMetadata(metadata) {
  const lm = metadata?.listMessage;
  if (!lm) return null;
  if (typeof lm === "object" && Array.isArray(lm.sections)) {
    return lm.sections
      .map((sec) => ({
        title: String(sec?.title || "").trim(),
        rows: (Array.isArray(sec?.rows) ? sec.rows : [])
          .map((r) => ({
            id: String(r?.id || r?.rowId || "").trim(),
            title: String(r?.title || r?.nome || r?.label || "").trim(),
            description: String(r?.description || r?.descricao || "").trim(),
          }))
          .filter((r) => r.title),
      }))
      .filter((s) => s.rows.length);
  }
  if (Array.isArray(lm) && lm.length) {
    const rows = lm
      .map((x) => {
        if (typeof x === "string") return { id: "", title: x.trim(), description: "" };
        return {
          id: String(x?.id || "").trim(),
          title: String(x?.title || x?.nome || x?.label || "").trim(),
          description: String(x?.description || "").trim(),
        };
      })
      .filter((r) => r.title);
    return rows.length ? [{ title: "", rows }] : null;
  }
  return null;
}

function ActionRow({ disabled, onClick, children, href }) {
  const commonSx = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.75,
    py: 1.15,
    px: 1.5,
    color: WA_GREEN,
    fontWeight: 500,
    fontSize: "0.95rem",
    textAlign: "center",
    bgcolor: "transparent",
    "&:disabled": { opacity: 0.45 },
  };
  if (href) {
    return (
      <ButtonBase
        component={Link}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        disabled={disabled}
        sx={{ ...commonSx, textDecoration: "none", color: WA_GREEN }}
      >
        {children}
      </ButtonBase>
    );
  }
  return (
    <ButtonBase disabled={disabled} onClick={onClick} sx={commonSx}>
      {children}
    </ButtonBase>
  );
}

function isTicketFlowWeb(metadata) {
  const ix = metadata?.interactiveOptions;
  return Boolean(ix && typeof ix === "object" && ix.mode === "ticket_flow_web" && ix.flowToken);
}

export default function WebChatInteractive({ metadata, disabled = false, onSelect, onOpenTicketFlow }) {
  const { t } = useTranslation("chat");
  const [open, setOpen] = useState(false);
  const carouselCards = Array.isArray(metadata?.carouselMessage?.cards) ? metadata.carouselMessage.cards : [];
  const buttonOptions = useMemo(() => getChatbotInteractiveOptions(metadata), [metadata]);
  const buttons = useMemo(
    () => buttonOptions.map((o) => o.label).filter(Boolean),
    [buttonOptions]
  );
  const ticketFlowEarly = isTicketFlowWeb(metadata);
  const linkButtons = useMemo(
    () => (ticketFlowEarly ? [] : getChatbotLinkButtons(metadata)),
    [metadata, ticketFlowEarly]
  );
  const sections = useMemo(() => listSectionsFromMetadata(metadata), [metadata]);

  if (carouselCards.length) {
    return (
      <Box sx={{ display: "flex", gap: 1, overflowX: "auto", mt: 0.75, pb: 0.5 }}>
        {carouselCards.map((card, index) => (
          <Box
            key={`${card.postback || index}`}
            sx={{
              flex: "0 0 180px",
              border: "1px solid rgba(0,0,0,0.08)",
              borderRadius: 1,
              overflow: "hidden",
              bgcolor: "#fff",
            }}
          >
            {card.imageUrl ? (
              <Box
                component="img"
                src={card.imageUrl}
                alt=""
                sx={{ width: "100%", height: 84, objectFit: "cover", display: "block" }}
              />
            ) : null}
            <Box sx={{ px: 1, py: 0.75 }}>
              <Typography sx={{ fontSize: "0.8rem", whiteSpace: "pre-line" }}>{card.body}</Typography>
              <ButtonBase
                disabled={disabled}
                onClick={() => onSelect?.(String(card.postback || index + 1))}
                sx={{ width: "100%", mt: 0.5, py: 0.5, color: WA_GREEN, fontWeight: 600, fontSize: "0.8rem" }}
              >
                {card.buttonTitle || "Escolher"}
              </ButtonBase>
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  const ticketFlow = ticketFlowEarly;
  const ticketFlowToken = ticketFlow ? String(metadata.interactiveOptions.flowToken || "").trim() : "";
  const ctaUrl =
    metadata?.interactiveOptions && typeof metadata.interactiveOptions === "object"
      ? String(metadata.interactiveOptions.ctaUrl || metadata.interactiveOptions.url || "").trim()
      : "";
  const ctaLabel =
    metadata?.interactiveOptions && typeof metadata.interactiveOptions === "object"
      ? String(
          metadata.interactiveOptions.flowCta ||
            metadata.interactiveOptions.ctaDisplayText ||
            metadata.interactiveOptions.ctaLabel ||
            metadata.interactiveOptions.label ||
            metadata.interactiveOptions.title ||
            (ticketFlow ? t("webchat.buyTicket") : t("webchat.continueCta"))
        ).trim()
      : t("webchat.continueCta");
  const hasLegacyCta = Boolean(ctaUrl) && !ticketFlow && !linkButtons.some((item) => item.url === ctaUrl);

  const listButtonText = resolveListButtonLabel(
    metadata?.listMessage && typeof metadata.listMessage === "object"
      ? metadata.listMessage.buttonText || metadata.listMessage.button
      : "",
    t
  );
  const useListSheet = Boolean(sections?.length) || buttons.length > INLINE_BUTTON_LIMIT;
  const inlineButtons = useListSheet ? [] : buttons;

  if (!buttons.length && !linkButtons.length && !hasLegacyCta && !sections?.length && !ticketFlow) {
    return null;
  }

  const pick = (option) => {
    setOpen(false);
    if (option == null) return;
    if (typeof option === "string") {
      onSelect?.(option);
      return;
    }
    const id = String(option.id || "").trim();
    const label = String(option.label || option.title || "").trim();
    onSelect?.(id || label);
  };

  const rows = [];

  inlineButtons.forEach((label, idx) => {
    const opt = buttonOptions[idx] || { id: "", label };
    rows.push(
      <ActionRow key={`btn-${idx}-${label}`} disabled={disabled} onClick={() => pick(opt)}>
        <ReplyIcon sx={{ fontSize: 18, transform: "scaleX(-1)", color: WA_GREEN }} />
        {label}
      </ActionRow>
    );
  });

  if (useListSheet) {
    rows.push(
      <ActionRow key="list-open" disabled={disabled} onClick={() => setOpen(true)}>
        <ListAltIcon sx={{ fontSize: 18, color: WA_GREEN }} />
        {listButtonText}
      </ActionRow>
    );
  }

  linkButtons.forEach((linkBtn, idx) => {
    rows.push(
      <ActionRow key={`link-${idx}`} href={linkBtn.url} disabled={disabled}>
        <OpenInNewIcon sx={{ fontSize: 16 }} />
        {linkBtn.label || t("webchat.continueCta")}
      </ActionRow>
    );
  });

  if (ticketFlow) {
    rows.push(
      <ActionRow
        key="ticket-flow"
        disabled={disabled}
        href={onOpenTicketFlow ? undefined : ctaUrl || undefined}
        onClick={
          onOpenTicketFlow
            ? () => onOpenTicketFlow(ticketFlowToken)
            : undefined
        }
      >
        <OpenInNewIcon sx={{ fontSize: 16 }} />
        {ctaLabel || t("webchat.buyTicket")}
      </ActionRow>
    );
  }

  if (hasLegacyCta) {
    rows.push(
      <ActionRow key="legacy-cta" href={ctaUrl} disabled={disabled}>
        <OpenInNewIcon sx={{ fontSize: 16 }} />
        {ctaLabel}
      </ActionRow>
    );
  }

  return (
    <>
      <Box sx={{ width: "calc(100% + 20px)", mx: -1.25, mt: 0.35, mb: 0 }}>
        {rows.map((row, idx) => (
          <Box key={row.key || idx}>
            <Divider sx={{ borderColor: "rgba(0,0,0,0.08)" }} />
            {row}
          </Box>
        ))}
      </Box>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "18px 18px 0 0",
            maxHeight: "72vh",
            pb: "max(12px, env(safe-area-inset-bottom))",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            py: 1.5,
            px: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>
            {t("webchat.seeOptions")}
          </Typography>
          <IconButton
            aria-label={t("webchat.close")}
            onClick={() => setOpen(false)}
            sx={{ position: "absolute", right: 8, top: 6 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ overflowY: "auto" }}>
          {sections?.length ? (
            sections.map((sec, sIdx) => (
              <Box key={`sec-${sIdx}`}>
                {sec.title ? (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      px: 2.5,
                      pt: 1.5,
                      pb: 0.5,
                      color: "text.secondary",
                      fontWeight: 600,
                    }}
                  >
                    {sec.title}
                  </Typography>
                ) : null}
                <List disablePadding>
                  {sec.rows.map((row, rIdx) => (
                    <ListItemButton
                      key={`${row.id || row.title}-${rIdx}`}
                      disabled={disabled}
                      onClick={() => pick({ id: row.id, label: row.title, title: row.title })}
                      sx={{ py: 1.5, px: 2.5 }}
                    >
                      <ListItemText
                        primary={row.title}
                        secondary={row.description || undefined}
                        primaryTypographyProps={{ fontWeight: 500, fontSize: "1rem" }}
                      />
                    </ListItemButton>
                  ))}
                </List>
                {sIdx < sections.length - 1 ? <Divider /> : null}
              </Box>
            ))
          ) : (
            <List disablePadding>
              {buttonOptions.map((opt, idx) => (
                <ListItemButton
                  key={`${idx}-${opt.label}`}
                  disabled={disabled}
                  onClick={() => pick(opt)}
                  sx={{ py: 1.5, px: 2.5 }}
                >
                  <ListItemText
                    primary={opt.label}
                    primaryTypographyProps={{ fontWeight: 500, fontSize: "1rem" }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
        <Typography
          sx={{
            textAlign: "center",
            color: "text.secondary",
            fontSize: "0.78rem",
            py: 1.5,
            px: 2,
          }}
        >
          {t("webchat.tapToSelect")}
        </Typography>
      </Drawer>
    </>
  );
}
